'use client';

/**
 * AdminToaster — the admin skin of the shared `packages/ui` Toaster (BL-1905).
 *
 * Mounted once, inside `ContentDock`, so toasts centre on the content area and
 * stack above the bulk bar. Fire one with `toast.success(...)` from
 * `@auctogon/ui/web`. Look per the Sep 23 "Bulk actions" handoff: a white card,
 * 1px gray-200 border, 8px radius, a green check, and a × that closes it; a
 * toast closes itself after five seconds (AC 7). The shadow is `shadow-xl`,
 * a step deeper than the handoff's `shadow-lg`, so the white card lifts off
 * the white table behind it (BL-1905 sign-off, Dan Saper 2026-09-26).
 */

import type { CSSProperties } from 'react';
import { CircleCheck } from 'lucide-react';
import { Toaster, type ToasterDialect } from '@auctogon/ui/web';

const ADMIN_TOASTER: ToasterDialect = {
    position: 'bottom-center',
    gap: 10,
    duration: 5_000,
    // The dock sets `--dock-toast-bottom` to lift the stack 10px above a bulk
    // bar when one is mounted (and above its error pill); 24px — the bar's own
    // bottom offset — otherwise. Sonner writes the string through verbatim.
    offset: { bottom: 'var(--dock-toast-bottom, 24px)' },
    icons: {
        // #16a34a (green-600) on #ffffff ........ 3.30:1  non-text ✓ (needs 3.0)
        // The handoff's #10b981 is 2.54:1 — below the ADR 051 floor for a
        // state icon — so the check takes the same green bidder's does.
        // 18px: the handoff's toast icon size (between the h-4 and h-5 steps).
        success: <CircleCheck className="h-[18px] w-[18px] text-green-600" aria-hidden />,
    },
    toastOptions: {
        // Unstyled: every class below is admin's, so the card cannot drift
        // with a Sonner upgrade. The close button renders first in Sonner's
        // markup; `order-last` moves it to the end of the row.
        unstyled: true,
        classNames: {
            toast:
                'pointer-events-auto flex w-full items-center gap-density-control rounded-lg border border-gray-200 bg-white py-2.5 pl-3.5 pr-2.5 shadow-xl',
            // #111827 on #ffffff ................. 17.74:1  text ✓ (needs 4.5)
            title: 'text-body font-semibold text-gray-900',
            icon: 'flex shrink-0 items-center',
            content: 'min-w-0 flex-1',
            // A toast's one follow-up (BL-2428: "View" on a split's new invoice).
            // #09514d (--primary) on #ffffff ....... 9.15:1  text ✓ (needs 4.5)
            actionButton:
                'shrink-0 rounded-lg px-2.5 py-1 text-body font-medium text-primary transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            // #6b7280 on #ffffff .................. 4.83:1  non-text ✓ (needs 3.0)
            closeButton:
                'order-last flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900',
        },
    },
    // Sonner stacks toasts at a fixed width; 420px fits the longest bulk copy
    // ("Removed from Fall Harvest Sale · back in Inventory · 3 lots") on one line.
    style: { '--width': '420px' } as CSSProperties,
};

/**
 * The admin app's toast stack. Renders nothing until a `toast()` is fired.
 *
 * @returns The bottom-centred admin toast stack.
 */
export default function AdminToaster() {
    return <Toaster dialect={ADMIN_TOASTER} />;
}
